import React, { useState } from "react";
import { Trash2, AlertTriangle } from "lucide-react";
import Modal from "./Modal";
import { deleteTeacher } from "../../utils/deleteTeacher";
import { toast } from "react-toastify";

interface ConfirmDeleteTeacherModalProps {
  isOpen: boolean;
  onClose: () => void;
  teacher: {
    id: string;
    fullname: string;
    email: string;
    schoolName: string;
  } | null;
  onTeacherDeleted: () => void;
}

const ConfirmDeleteTeacherModal: React.FC<ConfirmDeleteTeacherModalProps> = ({
  isOpen,
  onClose,
  teacher,
  onTeacherDeleted,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!teacher) return;

    setIsDeleting(true);
    try {
      const { error } = await deleteTeacher(teacher.id);
      if (error) {
        toast.error("Failed to delete teacher: " + error);
        return;
      }

      toast.success("Teacher deleted successfully");
      onTeacherDeleted();
      onClose();
    } catch (error) {
      console.error("Error deleting teacher:", error);
      toast.error("Failed to delete teacher");
    } finally {
      setIsDeleting(false);
    }
  };

  if (!teacher) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Delete Teacher">
      <div className="space-y-4">
        {/* Warning Icon */}
        <div className="flex items-center justify-center">
          <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-full">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
        </div>

        {/* Warning Message */}
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Are you sure you want to delete this teacher?
          </h3>
          <p className="text-sm text-gray-600">
            This action cannot be undone. The teacher will be permanently
            removed from the system.
          </p>
        </div>

        {/* Teacher Info */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="text-sm">
            <div className="font-medium text-gray-900">{teacher.fullname}</div>
            <div className="text-gray-600">{teacher.email}</div>
            <div className="text-gray-600">{teacher.schoolName}</div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-4">
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={isDeleting}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDeleting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Teacher
              </>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ConfirmDeleteTeacherModal;
